import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { useReportStore } from '@store/index';

import { parseLoginError } from '@common/helpers';

import { LOGIN_FIELDS } from './constants';
import type { TField } from './types';

export const useLoginForm = () => {
  // Simple form validation to check if input fields are filled out.
  // When there is no text inputs:
  //   on each input focus red border appears until user enters text or until focus is lost. Then red border disappears.
  //   when submit is clicked if there is no text in the inputs error message and red border are displayed
  //   until there is text in the inputs.
  const [fields, setFields] = useState({
    // NOTE !!!: Reset default values when in production!
    purchaseOrderNo: { value: '0111-0101010', interacted: false },
    vaultKey: { value: 'n0tasecret', interacted: false },
  });

  const [loginError, setLoginError] = useState<{
    purchaseOrderNo: string;
    vaultKey: string;
  }>({
    purchaseOrderNo: '',
    vaultKey: '',
  });

  // For Global Alert box
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const navigate = useNavigate();
  const { loadReport, reportStoreReset, error } = useReportStore();

  const clearGlobalError = () => {
    return setGlobalError(null);
  };

  const onFocus = (field: TField) => {
    setFields((prev) => {
      return {
        ...prev,
        [field]: { ...prev[field], interacted: true },
      };
    });
  };

  const onBlur = (field: TField) => {
    if (!fields[field].value.trim()) {
      setLoginError((prev) => {
        return {
          ...prev,
          [field]: field === LOGIN_FIELDS.PURCHASE_ORDER_NO ? 'Report ID is required' : 'Key is required',
        };
      });
    }
  };

  const onChange = (field: TField) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFields((prev) => {
        return {
          ...prev,
          [field]: { ...prev[field], value },
        };
      });

      setLoginError((prev) => {
        return { ...prev, [field]: '' };
      });
      if (globalError) {
        setGlobalError(null);
      }
    };
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Reset store on each submit
    // to avoid data leaking between different reports
    reportStoreReset();
    clearGlobalError();

    const poValue = fields.purchaseOrderNo.value.trim();
    const keyValue = fields.vaultKey.value.trim();

    const poError = !poValue ? 'Report ID is required' : '';
    const keyError = !keyValue ? 'Key is required' : '';

    setLoginError({ purchaseOrderNo: poError, vaultKey: keyError });

    if (poError || keyError) {
      return;
    }

    setIsReportLoading(true);

    try {
      const reportLoaded = await loadReport({
        po_number: poValue,
        secret: keyValue,
      });

      if (reportLoaded) {
        navigate(`/loading/${poValue}`, { viewTransition: true });
      } else {
        setTimeout(() => {
          // simulating errors here with a specific error response.
          // we should handle this properly when we work on error message handling.
          setGlobalError(
            parseLoginError({ error: 'some not important error message', status: poValue === 'something' ? 400 : 404 })
          );
        }, 3000);
      }
    } catch (err) {
      setGlobalError(parseLoginError(error || err));
    } finally {
      setTimeout(() => {
        setIsReportLoading(false);
      }, 3000);
    }
  };

  const showBorder = {
    purchaseOrderNo: !!loginError.purchaseOrderNo,
    vaultKey: !!loginError.vaultKey,
  };

  return {
    purchaseOrderNo: fields.purchaseOrderNo.value,
    vaultKey: fields.vaultKey.value,
    error: loginError,
    globalError,
    showBorder,
    onChange,
    onBlur,
    onFocus,
    onSubmit,
    isReportLoading,
    clearGlobalError,
  };
};
